import {lazy, ReactNode, Suspense} from 'react';
import './App.css';
import {BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import {Authenticator} from '@aws-amplify/ui-react';
import {ThemeProvider} from '@mui/material';
import * as Sentry from '@sentry/react';
import {createTheme} from '@mui/material/styles';
import {primaryAction, secondaryAction} from './components/Colours';
import {Alerts} from './components/alerts/Alerts';
import Loader from './components/Loader';
import {useCssTheme, useDarkMode} from './temporary/localStorageUtils';
import {Fallback} from "./components/fallback/Fallback.tsx";
import {TEST_ENVIRONMENT} from "./common/config.ts";
import Head from "./components/head/Head.tsx";

const ThreeJsPages = lazy(() => import('./ThreeJsPages'));
const Landing = lazy(() => import('./landing/Landing'));
const LegalNotice = lazy(() => import('./landing/other/./LegalNotice.tsx'));
const CreativeLicense = lazy(() => import('./landing/other/CreativeLicense'));
const TermsOfService = lazy(() => import('./landing/other/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./landing/other/PrivacyPolicy'));

function App() {
  return (
    <CssThemeProvider>
      <Authenticator.Provider>
        <AppRoutes/>
      </Authenticator.Provider>
    </CssThemeProvider>
  );
}

function AppRoutes() {
  const darkMode = useDarkMode();
  const theme = createTheme({
    palette: {
      primary: {
        main: primaryAction(darkMode)
      },
      secondary: {
        main: secondaryAction(darkMode)
      },
      info: {
        main: primaryAction(darkMode),
      },
      mode: darkMode ? 'dark' : undefined
    },
    components: {
      // Name of the component
      MuiTableCell: {
        styleOverrides: {
          // Name of the slot
          root: {
            // Some CSS
            backgroundColor: 'var(--background)'
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          // Name of the slot
          root: {
            // Some CSS
            minWidth: '320px'
          }
        }
      }
    },
    typography: {
      fontFamily: ['"Noto Sans JP"', 'sans-serif'].join(',')
    }
  });
  return (
    <>
      {TEST_ENVIRONMENT && <meta name="robots" content="noindex"/>}
      <ThemeProvider theme={theme}>
        <Sentry.ErrorBoundary fallback={error => <Fallback error={error}/>}>
          <Suspense fallback={
            <Loader isRequesting>
              <Head title="Fantasy Town Generator" noSuffix/>
            </Loader>
          }>
            <Router>
              <Routes>
                <Route path="/" element={<Landing/>}/>
                <Route path="/*" element={<ThreeJsPages/>}/>
                <Route path="/legal-notice" element={<LegalNotice/>}/>
                <Route path="/creative-license" element={<CreativeLicense/>}/>
                <Route path="/terms-of-service" element={<TermsOfService/>}/>
                <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
              </Routes>
            </Router>
          </Suspense>
          <Alerts/>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </>
  );
}

interface CssThemeProviderProps {
  children: ReactNode
}

function CssThemeProvider({children}: CssThemeProviderProps) {
  const {theme} = useCssTheme();
  return (
    <div className={`root-component default-color-theme default-font-theme ${theme}`}>
      {children}
    </div>
  );
}

export default App;
